import { useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import Lottie, { type LottieRefCurrentProps } from 'lottie-react';
import { Button } from '@/components/atoms/Button';
import { StatCard } from '@/components/molecules/StatCard';
import { X } from 'lucide-react';
import { Icon } from '@/components/atoms/Icon';
import successAnimation from '@/assets/lottie/success.json';
import { ROUTES, buildRoute } from '@/constants/routes';
import {
  storyResourcesEntrance,
  entranceAwareBlockTransition,
} from '@/constants/storyResourcesEntrance';
import { usePageTransitionEntrance } from '@/context/PageTransitionEntranceContext';
import { useStoryBottomBar } from '@/context/StoryBottomBarContext';
import type { ActivityResult, LearningObjective, UserAnswer } from '@/types/story.types';
import type { MistakeReviewSessionPayload } from '@/types/mistakeReview.types';
import { ObjectiveAchievements, type ObjectiveAchievement } from './components/ObjectiveAchievements';
import styles from './ResultPage.module.scss';

interface ResultState {
  source: 'story' | 'resource';
  storyId?: string;
  resourceId?: string;
  storyTitle: string;
  score: number;
  totalPoints: number;
  earnedPoints: number;
  xpEarned: number;
  /** Solo conversaciones-chat: valor del intento (piso + precisión), antes de
   * la resta aditiva del wallet. En primera vez coincide con `xpEarned`. */
  xpFull?: number;
  timeSpent: number;
  isPassed: boolean;
  /** `activityResults` del submit (incluye userAnswer por actividad) */
  answers?: ActivityResult[];
  fromStoriesTab?: 'weekly' | 'all';
  fromDashboard?: boolean;
  scrollY?: number;
  viewMode?: 'card' | 'list';
  allAssignmentsNextOffset?: number;
  allAssignmentsHasMore?: boolean;
  fromReview?: boolean;
  reviewDueLoadedCount?: number;
  reviewDueHasMore?: boolean;
  /** Paginación de la lista de conversaciones (restaurar al volver). */
  forYouLoaded?: number;
  moreLoaded?: number;
  /** Learning objectives for this challenge (optional — legacy challenges may not have them) */
  learningObjectives?: LearningObjective[];
  /** Maps activityId → learningObjectiveId for per-objective achievement computation */
  activityObjectiveMap?: Record<string, string>;
}

function conversationDetailStudentPath(r: { resourceId?: string }): string {
  if (!r.resourceId) return ROUTES.STUDENT.CONVERSATIONS;
  return buildRoute(ROUTES.STUDENT.CONVERSATION, { id: r.resourceId });
}

/** Types that the student can actually fail — informational cards are always correct. */
const GRADABLE_ACTIVITY_TYPES = new Set([
  'multiple_choice',
  'true_false',
  'fill_in_blank_tap',
  'matching',
  'ordering',
  'audio_translation_select',
  'audio_ordering',
  'audio_dictation',
  'translation_tap',
  'translation_select',
  'sentence_complete_tap',
  // vocabulary_card: informational, always auto-completed, not gradable
]);

/** Una fila por actividad (la última gana) por si el payload trajera duplicados. */
function dedupeActivityResultsById(rows: ActivityResult[]): ActivityResult[] {
  const lastById = new Map<string, ActivityResult>();
  for (const row of rows) {
    if (!row.activityId) continue;
    lastById.set(row.activityId, row);
  }
  return [...lastById.values()];
}

function buildMistakeReviewSessionFromResult(r: ResultState): MistakeReviewSessionPayload | null {
  const deduped = dedupeActivityResultsById(r.answers ?? []);
  const gradable = deduped.filter((a) => GRADABLE_ACTIVITY_TYPES.has(a.activityType ?? ''));
  const wrongOrdered = gradable.filter((a) => a.isCorrect === false).map((a) => a.activityId);
  const queueIds = [...new Set(wrongOrdered)];
  if (queueIds.length === 0) return null;
  const inQueue = new Set(queueIds);
  const preservedAnswers: Record<string, UserAnswer> = {};
  for (const row of gradable) {
    if (row.isCorrect && row.userAnswer && !inQueue.has(row.activityId)) {
      preservedAnswers[row.activityId] = row.userAnswer;
    }
  }
  // vocabulary_card es informacional (no gradable) — siempre se preserva para que
  // allAnswered no bloquee el envío en sesiones de repaso
  for (const row of deduped) {
    if (row.activityType === 'vocabulary_card' && row.userAnswer) {
      preservedAnswers[row.activityId] = row.userAnswer;
    }
  }
  return { queueIds, preservedAnswers };
}

/** Contexto al volver al reto (lista, dashboard, repaso) + repaso solo fallos si aplica. */
function buildStoryReentryState(
  r: ResultState,
  mistakeReviewSession: MistakeReviewSessionPayload | null
) {
  return {
    fromStoriesTab: r.fromStoriesTab,
    fromDashboard: r.fromDashboard,
    fromReview: r.fromReview,
    scrollY: r.scrollY,
    viewMode: r.viewMode,
    forYouLoaded: r.forYouLoaded,
    moreLoaded: r.moreLoaded,
    reviewDueLoadedCount: r.reviewDueLoadedCount,
    reviewDueHasMore: r.reviewDueHasMore,
    ...(mistakeReviewSession ? { mistakeReviewSession } : {}),
  };
}

function computeObjectiveAchievements(
  objectives: LearningObjective[],
  activityResults: ActivityResult[],
  objectiveMap: Record<string, string>
): ObjectiveAchievement[] {
  return objectives.map((obj) => {
    const taggedIds = new Set(
      Object.entries(objectiveMap)
        .filter(([, objId]) => objId === obj.id)
        .map(([actId]) => actId)
    );
    const results = activityResults.filter((r) => taggedIds.has(r.activityId));
    const correct = results.filter((r) => r.isCorrect).length;
    const total = results.length;
    const pct = total > 0 ? Math.round((correct / total) * 100) : 0;
    return { objective: obj, correct, total, pct, achieved: pct >= 70 };
  });
}

const OFFSET_Y = 20;
const DURATION = 0.42;
/** Retraso global antes de que arranque la secuencia de entrada */
const ENTRANCE_START = 0.4;
/** Hueco tras el inicio del icono hasta el título (debe cubrir fade + spring del círculo) */
const AFTER_ICON_TO_TITLE = 0.9;
/** Orden: icono → título → subtítulo → cards → botones */
const DELAYS = {
  icon: ENTRANCE_START,
  title: ENTRANCE_START + AFTER_ICON_TO_TITLE,
  subtitle: ENTRANCE_START + AFTER_ICON_TO_TITLE + 0.16,
  pointsCard: ENTRANCE_START + AFTER_ICON_TO_TITLE + 0.32,
  accuracyCard: ENTRANCE_START + AFTER_ICON_TO_TITLE + 0.46,
  objectives: ENTRANCE_START + AFTER_ICON_TO_TITLE + 0.6,
  primaryBtn: ENTRANCE_START + AFTER_ICON_TO_TITLE + 0.76,
  secondaryBtn: ENTRANCE_START + AFTER_ICON_TO_TITLE + 0.9,
} as const;

const hiddenBlock = { opacity: 0, y: OFFSET_Y };
const visibleBlock = { opacity: 1, y: 0 };

/**
 * ResultPage (A-05)
 * Displays story completion results with score, XP, and breakdown
 *
 * TODO Phase 4+: Implement API integration for saving results
 */
export function ResultPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const pageReady = usePageTransitionEntrance();
  const reducedMotion = Boolean(useReducedMotion());
  const { setContent: setBottomBarContent, setHideFade } = useStoryBottomBar() ?? {};

  const result = location.state as ResultState | undefined;

  /** Reintentar de cero (sin repaso de fallos) — va a la portada del reto/recurso. */
  const handleRetry = useCallback(() => {
    const r = location.state as ResultState | undefined;
    if (!r) return;
    const source = r.source ?? 'story';
    if (source === 'resource' && r.resourceId) {
      navigate(conversationDetailStudentPath(r), {
        state: r.fromReview
          ? {
              fromReview: true,
              reviewDueLoadedCount: r.reviewDueLoadedCount,
              reviewDueHasMore: r.reviewDueHasMore,
              scrollY: r.scrollY,
              viewMode: r.viewMode,
            }
          : {
              scrollY: r.scrollY,
              viewMode: r.viewMode,
            },
      });
    } else if (r.storyId) {
      navigate(`/student/story/${r.storyId}`, {
        state: buildStoryReentryState(r, null),
      });
    }
  }, [navigate, location.state]);

  /** Mismo comportamiento que «Repasar y mejorar» en MistakesReviewDrawer: cola solo fallos, respuestas borradas */
  const handleReviewMistakes = useCallback(() => {
    const r = location.state as ResultState | undefined;
    if (!r) return;
    const mistakeReviewSession = buildMistakeReviewSessionFromResult(r);
    if (!mistakeReviewSession) {
      handleRetry();
      return;
    }
    const source = r.source ?? 'story';
    if (source === 'resource' && r.resourceId) {
      navigate(conversationDetailStudentPath(r), {
        state: r.fromReview
          ? {
              fromReview: true,
              reviewDueLoadedCount: r.reviewDueLoadedCount,
              reviewDueHasMore: r.reviewDueHasMore,
              scrollY: r.scrollY,
              viewMode: r.viewMode,
              mistakeReviewSession,
            }
          : {
              scrollY: r.scrollY,
              viewMode: r.viewMode,
              mistakeReviewSession,
            },
      });
    } else if (r.storyId) {
      navigate(`/student/story/${r.storyId}`, {
        state: buildStoryReentryState(r, mistakeReviewSession),
      });
    }
  }, [navigate, location.state, handleRetry]);

  const handleContinue = useCallback(() => {
    const r = location.state as ResultState | undefined;
    if (!r) return;
    const source = r.source ?? 'story';
    if (r.fromReview && (r.storyId || r.resourceId)) {
      navigate(ROUTES.STUDENT.REVIEW, {
        state: {
          returnFromReview: true,
          scrollToReviewSourceType:
            source === 'story' ? ('story' as const) : ('conversation' as const),
          scrollToReviewSourceId:
            source === 'story' ? r.storyId! : r.resourceId!,
          scrollY: r.scrollY,
          viewMode: r.viewMode,
          reviewDueLoadedCount: r.reviewDueLoadedCount,
          reviewDueHasMore: r.reviewDueHasMore,
        },
      });
      return;
    }
    if (source === 'resource') {
      navigate(ROUTES.STUDENT.CONVERSATIONS, {
        state: {
          scrollToResourceId: r.resourceId,
          scrollY: r.scrollY,
          viewMode: r.viewMode,
          // Restaurar las páginas cargadas en cada sección (igual que Historias).
          returnFromConversation: true,
          forYouLoaded: r.forYouLoaded,
          moreLoaded: r.moreLoaded,
        },
      });
      return;
    }
    if (r.fromDashboard) {
      navigate(ROUTES.STUDENT.DASHBOARD, { state: { returnFromStory: true } });
    } else if (r.fromStoriesTab) {
      navigate(ROUTES.STUDENT.STORIES, {
        state: {
          tab: r.fromStoriesTab,
          scrollToStoryId: r.storyId,
          scrollY: r.scrollY,
          returnFromStory: true,
          viewMode: r.viewMode,
          ...(r.fromStoriesTab === 'all'
            ? {
                forYouLoaded: r.forYouLoaded,
                moreLoaded: r.moreLoaded,
              }
            : {}),
        },
      });
    } else {
      navigate(ROUTES.STUDENT.STORIES, {
        state: {
          tab: 'all',
          scrollToStoryId: r.storyId,
          returnFromStory: true,
          viewMode: r.viewMode,
        },
      });
    }
  }, [navigate, location.state]);

  const handleLeaveAfterFailure = useCallback(() => {
    const r = location.state as ResultState | undefined;
    if (!r) return;
    if (r.fromReview) {
      navigate(ROUTES.STUDENT.REVIEW);
      return;
    }
    navigate(ROUTES.STUDENT.STORIES, {
      state: {
        tab: r.fromStoriesTab ?? 'weekly',
        // Restaurar el scroll a la card de origen, igual que la ruta de éxito
        // (handleContinue). Sin esto, returnFromStory salta el scroll-to-top del
        // wrapper pero la lista no tiene a qué volver → se queda mal posicionada.
        scrollToStoryId: r.storyId,
        scrollY: r.scrollY,
        returnFromStory: true,
        viewMode: r.viewMode,
        ...(r.fromStoriesTab === 'all'
          ? {
              forYouLoaded: r.forYouLoaded,
              moreLoaded: r.moreLoaded,
            }
          : {}),
      },
    });
  }, [navigate, location.state]);

  useEffect(() => {
    setHideFade?.(true);
    return () => setHideFade?.(false);
  }, [setHideFade]);

  useEffect(() => {
    const scrollContainer = document.querySelector('[data-story-scroll-container]');
    if (scrollContainer) {
      scrollContainer.scrollTop = 0;
    } else {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }
  }, []);

  useEffect(() => {
    if (!result) return;
    const deduped = dedupeActivityResultsById(result.answers ?? []);
    const wrongCount = deduped.filter(
      (a) => a.isCorrect === false && GRADABLE_ACTIVITY_TYPES.has(a.activityType ?? '')
    ).length;
    const hasMistakes = wrongCount > 0;
    const passedWithMistakes = result.isPassed && hasMistakes;
    const failedWithMistakes = !result.isPassed && hasMistakes;

    const primaryTransition = entranceAwareBlockTransition(
      pageReady,
      reducedMotion,
      DURATION,
      DELAYS.primaryBtn
    );
    const secondaryTransition = entranceAwareBlockTransition(
      pageReady,
      reducedMotion,
      DURATION,
      DELAYS.secondaryBtn
    );

    let primaryLabel: string;
    let primaryAction: () => void;
    let secondaryLabel: string | null = null;
    let secondaryAction: (() => void) | null = null;

    if (result.isPassed) {
      primaryLabel = t('resultPage.continueLearning');
      primaryAction = handleContinue;
      if (passedWithMistakes) {
        secondaryLabel = t('resultPage.reviewErrors');
        secondaryAction = handleReviewMistakes;
      }
    } else if (failedWithMistakes) {
      primaryLabel = t('resultPage.reviewErrors');
      primaryAction = handleReviewMistakes;
      secondaryLabel = result.fromReview
        ? t('resultPage.goToReview')
        : t('resultPage.goToStories');
      secondaryAction = handleLeaveAfterFailure;
    } else {
      primaryLabel = (result.source ?? 'story') === 'resource'
        ? t('resultPage.retryResource')
        : t('resultPage.retryStory');
      primaryAction = handleRetry;
      secondaryLabel = result.fromReview
        ? t('resultPage.goToReview')
        : t('resultPage.goToStories');
      secondaryAction = handleLeaveAfterFailure;
    }

    setBottomBarContent?.(
      <div className={styles.result__startBar}>
        <motion.div
          className={styles.result__portalButton}
          initial={hiddenBlock}
          animate={pageReady ? visibleBlock : hiddenBlock}
          transition={primaryTransition}
        >
          <Button variant="primary" size="lg" fullWidth onClick={primaryAction}>
            {primaryLabel}
          </Button>
        </motion.div>
        {secondaryLabel && secondaryAction && (
          <motion.div
            className={styles.result__portalButton}
            initial={hiddenBlock}
            animate={pageReady ? visibleBlock : hiddenBlock}
            transition={secondaryTransition}
          >
            <Button variant="ghost" size="lg" fullWidth onClick={secondaryAction}>
              {secondaryLabel}
            </Button>
          </motion.div>
        )}
      </div>
    );
    return () => setBottomBarContent?.(null);
  }, [
    result,
    pageReady,
    reducedMotion,
    handleRetry,
    handleReviewMistakes,
    handleContinue,
    handleLeaveAfterFailure,
    setBottomBarContent,
    t,
  ]);

  const accuracyPercent = useMemo(() => {
    if (!result) return 0;
    // Historias/actividades: aciertos = puntos de actividad conseguidos.
    // Chat de conversación (sin actividades, totalPoints=0): la nota `score`
    // ya es la precisión a la 1ª (F/S×100).
    if (result.totalPoints > 0) {
      return Math.round((result.earnedPoints / result.totalPoints) * 100);
    }
    return result.score;
  }, [result]);

  // Card "puntos obtenidos": puntos de actividad en historias; en un chat sin
  // actividades mostramos lo que VALE el intento (`xpFull`: piso + precisión),
  // no el delta aditivo del wallet — así la card es coherente con la precisión
  // del intento aunque en un reintento el wallet sume menos. Fallback a
  // `xpEarned` si el backend aún no envía `xpFull`.
  const pointsEarnedValue = useMemo(() => {
    if (!result) return 0;
    return result.totalPoints > 0
      ? result.earnedPoints
      : result.xpFull ?? result.xpEarned;
  }, [result]);

  const objectiveAchievements = useMemo(() => {
    if (
      !result?.learningObjectives?.length ||
      !result.activityObjectiveMap ||
      !result.answers?.length
    ) {
      return null;
    }
    const deduped = dedupeActivityResultsById(result.answers);
    return computeObjectiveAchievements(
      result.learningObjectives,
      deduped,
      result.activityObjectiveMap
    );
  }, [result]);

  const fadeBlockMemo = useCallback(
    (delay: number) => ({
      initial: hiddenBlock,
      animate: pageReady ? visibleBlock : hiddenBlock,
      transition: entranceAwareBlockTransition(pageReady, reducedMotion, DURATION, delay),
    }),
    [pageReady, reducedMotion]
  );

  const successLottieRef = useRef<LottieRefCurrentProps>(null);

  // Sincronizamos el trazo del check con la entrada del círculo (DELAYS.icon) y
  // respetamos reduce-motion mostrando directamente el fotograma final.
  useEffect(() => {
    if (!result?.isPassed) return;
    const lottie = successLottieRef.current;
    if (!lottie) return;

    if (reducedMotion) {
      const lastFrame = Math.max(0, (lottie.getDuration(true) ?? 1) - 1);
      lottie.goToAndStop(lastFrame, true);
      return;
    }
    if (!pageReady) return;

    const timer = setTimeout(() => {
      lottie.goToAndStop(0, true);
      lottie.play();
    }, DELAYS.icon * 1000);
    return () => clearTimeout(timer);
  }, [result?.isPassed, pageReady, reducedMotion]);

  if (!result) {
    navigate(ROUTES.STUDENT.DASHBOARD);
    return null;
  }

  const isPassed = result.isPassed;

  const iconHidden = { opacity: 0, scale: 0.5 };
  const iconVisible = { opacity: 1, scale: 1 };

  return (
    <div className={styles.result}>
      <div className={styles.result__header}>
        <motion.div
          className={`${styles.result__headerStatusCircle} ${
            isPassed
              ? styles['result__headerStatusCircle--lottie']
              : styles['result__headerStatusCircle--incorrect']
          }`}
          aria-hidden
          initial={iconHidden}
          animate={pageReady ? iconVisible : iconHidden}
          transition={
            reducedMotion || !pageReady
              ? { duration: 0 }
              : {
                  delay: DELAYS.icon,
                  opacity: { duration: 0.58, ease: storyResourcesEntrance.ease },
                  scale: { type: 'spring', stiffness: 140, damping: 26, mass: 1.35 },
                }
          }
        >
          {isPassed ? (
            <Lottie
              lottieRef={successLottieRef}
              animationData={successAnimation}
              loop={false}
              autoplay={false}
              className={styles.result__statusLottie}
            />
          ) : (
            <Icon icon={X} size="xl" className={styles.result__statusIconGlyph} />
          )}
        </motion.div>
        <motion.h1 className={styles.result__headerTitle} {...fadeBlockMemo(DELAYS.title)}>
          {/* `source: 'resource'` hoy solo lo usan las conversaciones-chat. */}
          {result.isPassed
            ? (result.source ?? 'story') === 'resource'
              ? t('resultPage.conversationCompleted')
              : t('resultPage.storyCompleted')
            : (result.source ?? 'story') === 'resource'
              ? t('resultPage.conversationFailed')
              : t('resultPage.storyFailed')}
        </motion.h1>
        <motion.p className={styles.result__headerSubtitle} {...fadeBlockMemo(DELAYS.subtitle)}>
          {result.isPassed ? t('resultPage.passedSubtitle') : t('resultPage.failedSubtitle')}
        </motion.p>
      </div>

      <div className={styles.result__statsGrid}>
        <motion.div className={styles.result__statMotionWrap} {...fadeBlockMemo(DELAYS.pointsCard)}>
          <StatCard
            value={String(pointsEarnedValue)}
            centerContent
            surface="sunken"
            label={t('resultPage.pointsEarned')}
          />
        </motion.div>
        <motion.div className={styles.result__statMotionWrap} {...fadeBlockMemo(DELAYS.accuracyCard)}>
          <StatCard
            value={String(accuracyPercent)}
            secondaryValue="%"
            compactSecondary
            centerContent
            surface="sunken"
            label={t('resultPage.accuracy')}
          />
        </motion.div>
      </div>

      {objectiveAchievements && objectiveAchievements.length > 0 && (
        <motion.div {...fadeBlockMemo(DELAYS.objectives)}>
          <ObjectiveAchievements achievements={objectiveAchievements} />
        </motion.div>
      )}
    </div>
  );
}

export default ResultPage;
